import type {
  AdjustedFigures,
  LinkablePayIn,
  MovementEffectiveView,
  ShiftCorrectionView,
  ShiftPaymentView,
  ShiftReversalView,
  UnpaidOrderView,
} from './corrections';
import type { CashMovementKind, ShiftCloseKind, ShiftOpenKind, ShiftStatus } from './shifts';

/**
 * The owner's review and the non-cash check (Phase 7A-2A, ADR-026). Mirrors
 * supabase/migrations/20260926003800_owner_review.sql: the verdicts, the marks, which verdicts an item
 * takes in which state, which rows a bulk "Mark all checked" may cover, and the recent-authentication
 * rule. The server decides; these let the pages offer only what it would accept.
 */

/* ── acknowledgements ── */
export const REVIEW_VERDICTS = ['ok', 'follow_up', 'resolved', 'not_mine'] as const;
export type ReviewVerdict = (typeof REVIEW_VERDICTS)[number];
export const REVIEW_VERDICT_LABELS: Record<ReviewVerdict, string> = {
  ok: 'OK',
  follow_up: 'Follow up',
  resolved: 'Resolved',
  not_mine: 'Not mine',
};

/** Red items whose OK takes a note: a money difference or an alert is never waved through silently. */
export const OK_NEEDS_NOTE_KINDS = [
  'close_diff',
  'opening_diff',
  'combined_diff',
  'takeover',
  'pin_removal_differs',
  'shortfall_week',
  'payouts_over',
  // the 7B alerts, and cash given back for a non-cash sale approved from the phone (ADR-034)
  'alert_reversal_walkout',
  'alert_refund_no_sale',
  'alert_short_cash_refunds',
  'alert_near_limit',
  'remote_cash_for_noncash',
] as const;
/** Red items that take no OK: the row is checked again and re-marked, or the day's PIN activity is confirmed. */
export const NO_OK_KINDS = ['reconcile_exception', 'pin_unconfirmed'] as const;

export function okNeedsNote(kind: string): boolean {
  return (OK_NEEDS_NOTE_KINDS as readonly string[]).includes(kind);
}

export type ReviewTier = 'red' | 'counted' | 'pin';
/** The latest acknowledgement of an item: none yet (open), made on other facts (changed), kept
 * listed (follow_up), disowned (not_mine) or cleared (closed). */
export type ReviewAckState = 'open' | 'changed' | 'follow_up' | 'not_mine' | 'closed';

export interface ReviewAckView {
  state: ReviewAckState;
  /** Still to be dealt with before the shift or day is marked reviewed. */
  blocking: boolean;
  verdict: ReviewVerdict | null;
  note?: string | null;
  ack_id?: string;
  acknowledged_at?: string;
  acknowledged_by?: { id: string; name: string };
}

export interface ReviewItemView {
  item_key: string;
  tier: ReviewTier;
  kind: string;
  title: string;
  amount_minor: number | null;
  employee: { id: string; name: string } | null;
  shift_id: string | null;
  business_date: string;
  facts: Record<string, unknown>;
  /** What is acknowledged: the facts as shown. */
  digest: string;
  since: string | null;
  /** Red items only (and PIN items in the Day view). */
  ack?: ReviewAckView | null;
}

/** Which verdicts the server would accept on an item in its current state, for this viewer. */
export function verdictsFor(
  item: Pick<ReviewItemView, 'tier' | 'kind' | 'ack'>,
  opts: { ownPin: boolean },
): ReviewVerdict[] {
  const state = item.ack?.state ?? 'open';
  if (item.tier === 'pin') {
    if (!opts.ownPin || state === 'closed') return [];
    return state === 'not_mine' ? ['resolved'] : ['not_mine'];
  }
  if (item.tier !== 'red' || state === 'closed') return [];
  const ok = !(NO_OK_KINDS as readonly string[]).includes(item.kind);
  if (state === 'follow_up') return ok ? ['ok', 'resolved'] : ['resolved'];
  if (state === 'not_mine') return ['resolved'];
  return ok ? ['ok', 'follow_up'] : ['follow_up'];
}

/** A verdict that clears an item (OK, Resolved) is a sensitive action: fresh authentication. */
export function verdictNeedsRecentAuth(verdict: ReviewVerdict): boolean {
  return verdict === 'ok' || verdict === 'resolved';
}

/** Whether a verdict needs a note before the server accepts it. */
export function verdictNeedsNote(verdict: ReviewVerdict, kind: string): boolean {
  if (verdict === 'follow_up' || verdict === 'resolved') return true;
  if (verdict === 'ok') return okNeedsNote(kind);
  return false;
}

/* ── the non-cash check ── */
export const RECONCILE_MARKS = ['matched', 'missing', 'mismatch', 'unverified'] as const;
export type ReconcileMark = (typeof RECONCILE_MARKS)[number];
export const RECONCILE_MARK_LABELS: Record<ReconcileMark, string> = {
  matched: 'Found',
  missing: 'Not in the app',
  mismatch: 'Different amount',
  unverified: 'Could not check',
};
/** A pair (a payment that never arrived, with its void; ADR-029) is checked the other way round:
 * nothing came in and nothing was sent, so "not in the app" is what is expected. */
export const PAIR_MARK_LABELS: Record<ReconcileMark, string> = {
  missing: 'Not in the app, as expected',
  matched: 'Found: it arrived after all',
  mismatch: 'Found, with a different amount',
  unverified: 'Could not check',
};

export interface ReconcileRow {
  payment_id?: string;
  reversal_id?: string;
  mark: ReconcileMark;
  /** Required for a different amount: what the provider's record shows. */
  observed_amount_minor?: number;
  /** Required for "could not check", and when an exception is found after all. */
  note?: string;
}

export const RECONCILE_BATCH_MAX = 500;
export const RECONCILE_ROWS_MAX = 50;

export interface NoncashMarkView {
  mark: ReconcileMark;
  observed_amount_minor: number | null;
  note: string | null;
  by: { id: string; name: string };
  at: string;
  batch_id: string | null;
  mark_id: string;
}

/** Who stated a give-back's reference or answer, and when (the owner's view only). */
export interface StatedByView {
  by: { id: string; name: string };
  at: string;
  by_owner: boolean;
}

export type NoncashRowKind = 'payment' | 'void' | 'refund' | 'pair';

export interface NoncashRowView {
  row_id: string;
  /** A refund is one transfer (its rows with one method); a pair is a payment that never arrived
   * with its void, checked as one row keyed by the payment (ADR-029). */
  row_kind: NoncashRowKind;
  /** Null for money the owner gave back outside any shift (ADR-033). */
  shift_id: string | null;
  business_date: string;
  order_id: string;
  order_number: string;
  method: string;
  method_kind: string;
  payment_method_id: string;
  amount_minor: number;
  reference_last4: string | null;
  recorded_at: string;
  employee: { id: string; name: string } | null;
  late_entry: boolean;
  possible_duplicate: boolean;
  /** Before the date the check started: listed, never due. */
  before_start: boolean;
  /** Listed under a corrected method (7A-2B): checked row by row, due from the correction. */
  corrected: boolean;
  correction_id: string | null;
  /** The method as recorded, when corrected. */
  recorded_method: string | null;
  due_from: string;
  /** Recorded by the owner after the close (ADR-028): due from the recording, checked row by row. */
  owner_entry: boolean;
  /** A cancelled refund: listed in the history, never due, never summed. */
  cancelled: boolean;
  cancel_id: string | null;
  /** The ledger rows behind the row: a transfer's rows, a void, a pair's void. */
  reversal_ids: string[];
  /** A void's answer, or a pair's; null when nobody has answered. */
  outcome: 'sent_back' | 'never_arrived' | 'withdrawn' | null;
  /** A pair's void. */
  void_id: string | null;
  /** The owner's withdrawal of a missed payment (ADR-028), with its void: nothing to check. */
  withdrawn: boolean;
  /** The latest mark is an exception (for a pair: anything but "not in the app"). */
  exception: boolean;
  reference_stated: StatedByView | null;
  outcome_stated: StatedByView | null;
  batch_eligible: boolean;
  mark: NoncashMarkView | null;
}

/** Mirrors app.noncash_exception_mark: a pair expects "not in the app"; any other row expects "found". */
export function isExceptionMark(rowKind: NoncashRowKind, mark: ReconcileMark | null): boolean {
  if (mark === null) return false;
  return rowKind === 'pair' ? mark !== 'missing' : mark !== 'matched';
}

/** The mark that says all is well for a row. */
export function expectedMark(rowKind: NoncashRowKind): ReconcileMark {
  return rowKind === 'pair' ? 'missing' : 'matched';
}

/** A mark that takes an exception back (a note and a fresh authentication, server-side). */
export function markClearsException(
  rowKind: NoncashRowKind,
  previous: ReconcileMark | null,
  mark: ReconcileMark,
): boolean {
  return isExceptionMark(rowKind, previous) && !isExceptionMark(rowKind, mark);
}

export function markLabel(rowKind: NoncashRowKind, mark: ReconcileMark): string {
  return rowKind === 'pair' ? PAIR_MARK_LABELS[mark] : RECONCILE_MARK_LABELS[mark];
}

/** Mirrors app.noncash_row_json.batch_eligible and the checks of cash.reconcile_all: an unmarked
 * payment (never money given back), not a late entry, not a possible duplicate, not from before the
 * check started, not cancelled. */
export function batchEligible(
  row: Pick<
    NoncashRowView,
    'row_kind' | 'mark' | 'late_entry' | 'possible_duplicate' | 'before_start' | 'corrected'
  > & { cancelled?: boolean },
): boolean {
  return (
    row.row_kind === 'payment' &&
    row.mark === null &&
    !row.corrected &&
    !row.late_entry &&
    !row.possible_duplicate &&
    !row.before_start &&
    !row.cancelled
  );
}

/** The rows a "Mark all checked" covers, with the total the merchant app must show. */
export function batchSummary<
  T extends Parameters<typeof batchEligible>[0] & { row_id: string; amount_minor: number },
>(rows: readonly T[]): { ids: string[]; count: number; total_minor: number } {
  const eligible = rows.filter(batchEligible);
  return {
    ids: eligible.map((r) => r.row_id),
    count: eligible.length,
    total_minor: eligible.reduce((sum, r) => sum + r.amount_minor, 0),
  };
}

/** Marks that need a note before the server accepts them (a different amount needs the amount seen):
 * "could not check", and a mark that takes an exception back. */
export function markNeedsNote(
  mark: ReconcileMark,
  previous: ReconcileMark | null,
  rowKind: NoncashRowKind = 'payment',
): boolean {
  if (mark === 'unverified') return true;
  return markClearsException(rowKind, previous, mark);
}

/* ── the owner's PIN day ── */
export interface PinDayConfirmation {
  item_key: string;
  digest: string;
  /** Cash handed to the owner at a close: what they actually received. */
  received_minor?: number;
}

export interface PinDayItemView extends ReviewItemView {
  tier: 'pin';
  confirmed: boolean;
  received_minor: number | null;
  ack: ReviewAckView;
}

export interface PinDayState {
  owner: { id: string; name: string };
  business_date: string;
  items: PinDayItemView[];
  total: number;
  unconfirmed: number;
  not_mine: number;
  confirmed: boolean;
  confirmation_id: string | null;
  confirmed_at: string | null;
  digest: string;
}

/** What "All mine" sends: every item not answered "not mine" (or resolved since), as shown. */
export function pinDayConfirmations(
  items: readonly Pick<PinDayItemView, 'item_key' | 'digest' | 'kind' | 'ack' | 'amount_minor'>[],
  received: Readonly<Record<string, number | undefined>> = {},
): PinDayConfirmation[] {
  return items
    .filter((i) => i.ack.state !== 'not_mine' && i.ack.state !== 'closed')
    .map((i) =>
      i.kind === 'pin.removal'
        ? {
            item_key: i.item_key,
            digest: i.digest,
            received_minor: received[i.item_key] ?? i.amount_minor ?? 0,
          }
        : { item_key: i.item_key, digest: i.digest },
    );
}

/* ── recent authentication ── */
/** One entry of the JWT `amr` claim (authentication methods reference). */
export interface AmrEntry {
  method: string;
  /** Seconds since the epoch. */
  timestamp: number;
}

/** Mirrors app.auth_recent: the newest authentication in the token is within the window. */
export function authRecent(
  amr: readonly AmrEntry[] | null | undefined,
  withinSeconds: number,
  nowMs: number = Date.now(),
): boolean {
  if (!amr || amr.length === 0) return false;
  const newest = Math.max(...amr.map((a) => (Number.isFinite(a.timestamp) ? a.timestamp : 0)));
  return newest >= nowMs / 1000 - withinSeconds;
}

/** Reads the `amr` claim out of a decoded JWT payload, tolerating a missing or malformed one. */
export function amrOf(claims: unknown): AmrEntry[] {
  if (!claims || typeof claims !== 'object') return [];
  const raw = (claims as { amr?: unknown }).amr;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is { method?: unknown; timestamp?: unknown } => !!a && typeof a === 'object')
    .map((a) => ({ method: String(a.method ?? ''), timestamp: Number(a.timestamp) }))
    .filter((a) => Number.isFinite(a.timestamp));
}

/* ── the pages' data ── */
export type ShiftReviewStatus = 'to_check' | 'not_reviewed' | 'reviewed' | 'changed';
export type DayReviewStatus = ShiftReviewStatus | 'nothing';

export const SHIFT_REVIEW_STATUS_LABELS: Record<ShiftReviewStatus, string> = {
  to_check: 'To check',
  not_reviewed: 'Not reviewed',
  reviewed: 'Reviewed',
  changed: 'Changed since',
};

export interface ShiftReviewState {
  status: ShiftReviewStatus;
  digest: string;
  red_open: number;
  follow_ups: number;
  items?: ReviewItemView[];
  reviewed_at: string | null;
  reviewed_via: 'shift' | 'day' | null;
  reviewed_by: { id: string; name: string } | null;
}

export interface DayReviewState {
  business_date: string;
  items: ReviewItemView[];
  red_open: number;
  follow_ups: number;
  closed_shifts: number;
  reviewed_shifts: number;
  shift_red_open: number;
  digest: string;
  status: DayReviewStatus;
}

export interface MethodFigures {
  method: string;
  kind: string;
  count: number;
  captured_minor: number;
  voided_minor: number;
  refunded_minor: number;
  net_minor: number;
}

/** A shift on the owner's lists (app.shift_list_row). */
export interface ShiftListRow {
  id: string;
  seq: number;
  status: ShiftStatus;
  business_date: string;
  drawer: { id: string; name: string } | null;
  cashier: { id: string; name: string };
  opened_at: string;
  closed_at: string | null;
  open_kind: ShiftOpenKind;
  close_kind: ShiftCloseKind | null;
  closed_by: { id: string; name: string } | null;
  opening_difference_minor: number | null;
  over_short_minor: number | null;
  counted_minor: number | null;
  expected_minor: number | null;
  /** Expected cash from today's rows (a late entry or a correction moves it after the close). */
  expected_now_minor: number;
  tolerance_minor: number;
  retained_minor: number | null;
  removed_minor: number | null;
  removed_to: 'owner' | 'safe' | null;
  /** Closed, and the rows moved since the close froze its figures (a late entry or a correction). */
  adjusted: boolean;
  /** The figures as corrected (7A-2B): the originals plus the active corrections. */
  adjusted_figures: AdjustedFigures;
  review: ShiftReviewState;
  unmarked_rows: number;
}

export interface CashFigures {
  cash_sales_minor: number;
  change_given_minor: number;
  cash_returned_minor: number;
  pay_ins_minor: number;
  pay_outs_minor: number;
  pay_outs_by_category: Record<string, number>;
  safe_drops_minor: number;
  expected_minor: number;
  late_entries: { count: number; amount_minor: number };
  /** Present only when the owner recorded money on the shift after its close (ADR-028). */
  owner_entries?: {
    payments: number;
    payments_minor: number;
    refunds: number;
    refunds_minor: number;
  };
  opening_count_minor: number | null;
  expected_opening_minor: number | null;
  opening_difference_minor: number | null;
  counted_minor: number | null;
  close_expected_minor: number | null;
  over_short_minor: number | null;
  retained_minor: number | null;
  removed_minor: number | null;
  removed_to: string | null;
  variance_reason: string | null;
  tolerance_minor: number;
  [key: string]: unknown;
}

/** app.shift_summary_json: the figures a close freezes (and the same, live). */
export interface ShiftSummaryView {
  shift: {
    id: string;
    seq: number;
    status: ShiftStatus;
    business_date: string;
    drawer: { id: string; name: string } | null;
    cashier: { id: string; name: string };
    opened_at: string;
    open_kind: ShiftOpenKind;
    closing_started_at: string | null;
    closed_at: string | null;
    close_kind: ShiftCloseKind | null;
    closed_by: { id: string; name: string } | null;
  };
  cash: CashFigures;
  methods: MethodFigures[];
  counts: {
    purpose: string;
    attempt: number;
    final: boolean;
    within_tolerance: boolean | null;
    counted_minor: number;
    difference_minor: number | null;
    by: string;
    at: string;
  }[];
  orders: {
    placed: number;
    voided: number;
    lines_voided: number;
    open_unpaid: number;
    served_unpaid: number;
    open_orders?: {
      id: string;
      order_number: string;
      status: string;
      payment_status: string;
      fulfilment_status: string;
      total_minor: number;
      paid_minor: number;
      placed_at: string;
    }[];
  };
  corrections: {
    action: string;
    entity_id: string;
    by: string | null;
    approver: string | null;
    reason: string | null;
    amount_minor: number | null;
    at: string;
  }[];
  others: {
    action: string;
    by: string | null;
    method?: string | null;
    category?: string | null;
    amount_minor: number;
    at: string;
  }[];
  discarded: {
    original_kind: string;
    amount_minor: number | null;
    method: string | null;
    reason: string;
    by: string | null;
    at: string;
  }[];
  generated_at: string;
}

export interface ShiftDetailMovement {
  id: string;
  kind: CashMovementKind;
  amount_minor: number;
  category: string | null;
  payee: string | null;
  reason: string | null;
  receipt_ref: string | null;
  envelope_no: number | null;
  removed_to: string | null;
  flags: Record<string, unknown>;
  voids_movement_id: string | null;
  voided: boolean;
  by: { id: string; name: string };
  approved_by: { id: string; name: string } | null;
  recorded_at: string;
  /** A corrected pay-in or pay-out: what it reads as now (null when uncorrected). */
  effective: MovementEffectiveView | null;
}

export interface ShiftDetail {
  ok: true;
  shift: ShiftListRow;
  frozen: ShiftSummaryView | null;
  live: ShiftSummaryView;
  now: {
    late_entries: number;
    expected_differs: boolean;
    expected_now_minor: number;
    corrected: boolean;
  };
  adjusted: AdjustedFigures;
  movements: ShiftDetailMovement[];
  /** The shift's captured payments, by effective method (to correct a method). */
  payments: ShiftPaymentView[];
  /** More than the first 1,000 payments exist: the list is cut. */
  payments_more: boolean;
  reversals: ShiftReversalView[];
  /** A closed shift: the location's open orders still owed money that existed before the close,
   * newest first, up to 100 (what a missed payment can be recorded on; ADR-028). */
  unpaid_orders: UnpaidOrderView[];
  /** Every correction on the shift, newest first, with its status. */
  corrections: ShiftCorrectionView[];
  /** Unlinked pay-ins on later shifts at the location (cash found later, repayments into a drawer). */
  linkable_pay_ins: LinkablePayIn[];
  noncash: NoncashRowView[];
  review: ShiftReviewState & { items: ReviewItemView[] };
  acks: {
    id: string;
    item_key: string;
    kind: string;
    verdict: ReviewVerdict;
    note: string | null;
    digest: string;
    by: { id: string; name: string };
    at: string;
  }[];
  reviews: {
    id: string;
    digest: string;
    via: 'shift' | 'day';
    by: { id: string; name: string };
    at: string;
  }[];
}

export interface ShiftListResult {
  ok: true;
  location_id: string;
  from: string;
  to: string;
  shifts: ShiftListRow[];
}

export interface DayView {
  ok: true;
  location_id: string;
  business_date: string;
  today: string;
  oldest_unreviewed_date: string | null;
  shifts: (ShiftListRow & {
    frozen: CashFigures | null;
    live: CashFigures;
    methods: MethodFigures[];
  })[];
  totals: {
    by_method: MethodFigures[];
    over_short_minor: number;
    opening_difference_minor: number;
    settled_minor: number;
    unsettled_minor: number;
    corrections: number;
    by_person: {
      employee: { id: string; name: string };
      close_over_short_minor: number;
      opening_difference_minor: number;
      within_tolerance_minor: number;
      shifts: number;
    }[];
    pay_outs_by_category: Record<string, number>;
    removed_to_owner_minor: number;
    removed_to_safe_minor: number;
    late_entries: { count: number; amount_minor: number };
    /** Money the owner recorded on the day's closed shifts (ADR-028): entries that stand. */
    owner_entries: { count: number; payments_minor: number; refunds_minor: number };
    discarded: number;
  };
  noncash: {
    rows: NoncashRowView[];
    unmarked_count: number;
    unmarked_minor: number;
    batch_count: number;
    batch_total_minor: number;
    batch_ids: string[];
  };
  pin: PinDayState[];
  review: DayReviewState;
  /** Money the owner gave back by GCash or transfer outside any shift, that date (ADR-033). */
  outside_shift?: {
    count: number;
    refunded_minor: number;
    /** One row per refund: its ledger rows of one method are one transfer (ADR-029). */
    rows: {
      reversal_id: string;
      reversal_ids: string[];
      order_id: string;
      order_number: string;
      method: string;
      amount_minor: number;
      reason: string;
      by: { id: string; name: string };
      recorded_at: string;
      reference_last4: string | null;
    }[];
  };
  /** Orders that day, but no shift: from before shifts were switched on. */
  before_shifts: boolean;
}

export interface ReviewQueue {
  ok: true;
  location_id: string;
  today: string;
  items: ReviewItemView[];
  /** Closed shifts not reviewed of the 59 business days before the last 31 (the newest 90 in all, ADR-039). */
  older_unreviewed_shifts: number;
}

export interface ReviewCounts {
  ok: true;
  today: string;
  red_open: number;
  follow_ups: number;
  unreviewed_shifts: number;
  oldest_unreviewed_date: string | null;
  unmarked_rows: number;
  unmarked_minor: number;
  /** Closed shifts not reviewed of the 59 business days before the last 31 (the newest 90 in all, ADR-039). */
  older_unreviewed_shifts: number;
}

/** Shift and day digests bind "Mark reviewed" to what was on screen; a digest mismatch is a reload. */
export function isReviewConflict(hint: unknown): hint is { review: string } {
  return (
    !!hint && typeof hint === 'object' && typeof (hint as { review?: unknown }).review === 'string'
  );
}
